import React, { useState, useEffect, useCallback } from 'react';
import { ClipPrompt } from '../../../api/structs/clip';
import API from '../../../api/api';
import { RenderProposal, RenderTemplate } from '../../../api/renderTemplates';
import { useToast } from '../../../hooks/useToast';
import { AutoResizeTextarea } from '../../ui';
import {
  CarouselStylePresetDraft,
  applyBuiltinCarouselBrandPresetToMetadata,
  applyRenderProposalToMetadata,
  applyRenderTemplateToMetadata,
  carouselLayoutVariants,
  carouselTextDensities,
  getCarouselStylePresetDraft,
  updateCarouselDraftMetadata,
} from './carouselStylePreset';
import { builtinGenericCarouselBrandPresets } from '../../../remotion/generic-carousel/brandPresets';

interface CarouselSettingsInlineProps {
  clipId: string;
  clip: ClipPrompt;
  onRefresh: () => void;
}

const fromPipes = (val: string) => val.replace(/\|/g, '\n');
const toPipes = (val: string) => val.replace(/\n/g, '|');

const inputClass =
  'w-full bg-zinc-900 border border-white/10 text-sm text-white px-3 py-2 focus:outline-none focus:border-white/25 font-mono';
const labelClass = 'block text-[10px] uppercase tracking-widest text-zinc-400 mb-1 font-mono';
const buttonClass =
  'border border-white/10 px-3 py-2 text-[11px] uppercase tracking-widest text-zinc-300 hover:border-white/25 disabled:opacity-40 disabled:cursor-not-allowed font-mono';

const BUILTIN_TEMPLATE_PREFIX = 'builtin:';

const formatLabel = (value: string) =>
  value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const fieldValue = (value: unknown) => (value === undefined || value === null ? '' : String(value));

const CarouselSettingsInline: React.FC<CarouselSettingsInlineProps> = ({ clipId, clip, onRefresh }) => {
  const toast = useToast();
  const [brandingTag, setBrandingTag] = useState('');
  const [headline, setHeadline] = useState('');
  const [draft, setDraft] = useState<CarouselStylePresetDraft>({});
  const [templates, setTemplates] = useState<RenderTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [proposals, setProposals] = useState<RenderProposal[]>([]);
  const [proposalInstruction, setProposalInstruction] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [savingProposal, setSavingProposal] = useState(false);

  useEffect(() => {
    setBrandingTag(fromPipes(String(clip.metadata?.brandingTag || '')));
    setHeadline(fromPipes(String(clip.metadata?.headline || '')));
    setDraft(getCarouselStylePresetDraft(clip.metadata));
  }, [clip.metadata]);

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const items = await API.listRenderTemplates({ kind: 'carousel' });
      setTemplates(items);
      if (!selectedTemplateId) {
        setSelectedTemplateId(`${BUILTIN_TEMPLATE_PREFIX}${builtinGenericCarouselBrandPresets[0]?.id || ''}`);
      }
    } catch (error: any) {
      toast({ text: `Failed to load templates: ${error.message}`, level: 'error' });
    } finally {
      setLoadingTemplates(false);
    }
  }, [selectedTemplateId, toast]);

  const loadProposals = useCallback(async () => {
    try {
      setProposals(await API.listRenderProposals(clipId));
    } catch (error: any) {
      toast({ text: `Failed to load proposals: ${error.message}`, level: 'error' });
    }
  }, [clipId, toast]);

  useEffect(() => {
    loadTemplates();
    loadProposals();
  }, [loadTemplates, loadProposals]);

  const saveMetadata = async (metadata: Record<string, unknown>, successText?: string) => {
    await API.editClipPrompt(clipId, { metadata });
    if (successText) {
      toast({ text: successText, level: 'success' });
    }
    onRefresh();
  };

  const handleBlur = async (key: 'brandingTag' | 'headline', value: string) => {
    const pipedValue = toPipes(value);
    if (pipedValue === String(clip.metadata?.[key] || '')) return;

    try {
      await saveMetadata(
        {
          ...clip.metadata,
          [key]: pipedValue,
        },
        `${key === 'brandingTag' ? 'Branding tag' : 'Headline'} saved`
      );
    } catch (error: any) {
      toast({ text: `Failed to save ${key}: ${error.message}`, level: 'error' });
    }
  };

  const commitDraft = async (nextDraft: CarouselStylePresetDraft) => {
    setDraft(nextDraft);
    try {
      await saveMetadata(updateCarouselDraftMetadata(clip.metadata, nextDraft));
    } catch (error: any) {
      toast({ text: `Failed to save style draft: ${error.message}`, level: 'error' });
    }
  };

  const patchDraft = (
    key: keyof CarouselStylePresetDraft,
    value: string | number | boolean | undefined,
    commit = false
  ) => {
    const nextDraft: CarouselStylePresetDraft = { ...draft };
    if (value === '' || value === undefined) {
      delete nextDraft[key];
    } else {
      (nextDraft as Record<string, unknown>)[key] = value;
    }
    setDraft(nextDraft);
    if (commit) {
      commitDraft(nextDraft);
    }
  };

  const commitNumber = (key: keyof CarouselStylePresetDraft, rawValue: string) => {
    if (!rawValue.trim()) {
      const nextDraft = { ...draft };
      delete nextDraft[key];
      commitDraft(nextDraft);
      return;
    }
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) {
      toast({ text: `${String(key)} must be a number`, level: 'error' });
      return;
    }
    commitDraft({ ...draft, [key]: parsed });
  };

  const applyTemplate = async () => {
    if (selectedTemplateId.startsWith(BUILTIN_TEMPLATE_PREFIX)) {
      const presetId = selectedTemplateId.slice(BUILTIN_TEMPLATE_PREFIX.length);
      const preset = builtinGenericCarouselBrandPresets.find((item) => item.id === presetId);
      if (!preset) return;
      try {
        await saveMetadata(
          applyBuiltinCarouselBrandPresetToMetadata(clip.metadata, preset),
          `Applied visual preset: ${preset.name}`
        );
      } catch (error: any) {
        toast({ text: `Failed to apply visual preset: ${error.message}`, level: 'error' });
      }
      return;
    }

    const template = templates.find((item) => item.id === selectedTemplateId);
    if (!template) return;
    try {
      await saveMetadata(
        applyRenderTemplateToMetadata(clip.metadata, template),
        `Applied saved template: ${template.name}`
      );
    } catch (error: any) {
      toast({ text: `Failed to apply saved template: ${error.message}`, level: 'error' });
    }
  };

  const saveTemplate = async () => {
    const name = templateName.trim();
    if (!name) {
      toast({ text: 'Template name is required', level: 'error' });
      return;
    }
    setSavingTemplate(true);
    try {
      const config: Record<string, unknown> = { ...draft };
      const template = await API.createRenderTemplate({
        name,
        kind: 'carousel',
        source: 'user_saved',
        status: 'active',
        config,
        created_from_clip_id: clipId,
      });
      setTemplateName('');
      await loadTemplates();
      setSelectedTemplateId(template.id);
      toast({ text: `Saved template: ${template.name}`, level: 'success' });
    } catch (error: any) {
      toast({ text: `Failed to save template: ${error.message}`, level: 'error' });
    } finally {
      setSavingTemplate(false);
    }
  };

  const createProposal = async () => {
    const instruction = proposalInstruction.trim();
    if (!instruction) {
      toast({ text: 'Proposal instruction is required', level: 'error' });
      return;
    }
    setSavingProposal(true);
    try {
      const config: Record<string, unknown> = { ...draft };
      await API.createRenderProposal({
        clip_id: clipId,
        kind: 'carousel_design',
        status: 'draft',
        instruction,
        source_template_id: selectedTemplateId || undefined,
        metadata_patch_json: {
          stylePresetDraft: config,
          stylePresetDirty: true,
        },
        template_config_json: config,
        validation_report_json: {
          mode: 'frontend_saved_draft',
        },
      });
      setProposalInstruction('');
      await loadProposals();
      toast({ text: 'Saved proposal draft', level: 'success' });
    } catch (error: any) {
      toast({ text: `Failed to save proposal draft: ${error.message}`, level: 'error' });
    } finally {
      setSavingProposal(false);
    }
  };

  const applyProposal = async (proposal: RenderProposal) => {
    if (!['draft', 'validated', 'approved'].includes(proposal.status)) {
      toast({ text: `Cannot apply ${proposal.status} proposal`, level: 'error' });
      return;
    }
    try {
      await saveMetadata(
        applyRenderProposalToMetadata(clip.metadata, proposal),
        `Applied proposal: ${proposal.status}`
      );
    } catch (error: any) {
      toast({ text: `Failed to apply proposal: ${error.message}`, level: 'error' });
    }
  };

  return (
    <div className="border border-white/10 bg-black p-4 mb-4">
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
          Carousel Settings
        </p>
        {clip.metadata?.stylePresetDirty ? (
          <span className="text-[10px] uppercase tracking-widest text-amber-400 font-mono">Draft Dirty</span>
        ) : (
          <span className="text-[10px] uppercase tracking-widest text-zinc-600 font-mono">Template Synced</span>
        )}
      </div>

      <div className="space-y-5">
        <section className="space-y-4">
          <div>
            <label className={labelClass}>Branding Tag</label>
            <AutoResizeTextarea
              className="w-full bg-zinc-900 border border-white/10 text-sm text-white px-3 py-2 resize-none focus:outline-none focus:border-white/25 font-sans"
              rows={1}
              value={brandingTag}
              onChange={(e) => setBrandingTag(e.target.value)}
              onBlur={(e) => handleBlur('brandingTag', e.target.value)}
              placeholder="e.g. Your Brand Name"
            />
          </div>

          <div>
            <label className={labelClass}>Headline</label>
            <AutoResizeTextarea
              className="w-full bg-zinc-900 border border-white/10 text-sm text-white px-3 py-2 resize-none focus:outline-none focus:border-white/25 font-sans"
              rows={1}
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              onBlur={(e) => handleBlur('headline', e.target.value)}
              placeholder="Main Headline..."
            />
          </div>
        </section>

        <section className="border-t border-white/10 pt-4 space-y-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-end">
            <div className="flex-1">
              <label className={labelClass}>Visual Template</label>
              <select
                className={inputClass}
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                disabled={loadingTemplates && templates.length === 0 && builtinGenericCarouselBrandPresets.length === 0}
              >
                <optgroup label="Built-in presets">
                  {builtinGenericCarouselBrandPresets.map((preset) => (
                    <option key={preset.id} value={`${BUILTIN_TEMPLATE_PREFIX}${preset.id}`}>
                      {preset.name}
                    </option>
                  ))}
                </optgroup>
                {templates.length > 0 && (
                  <optgroup label="Saved templates">
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                {templates.length === 0 && !loadingTemplates && <option value="" disabled>No saved templates yet</option>}
                {loadingTemplates && <option value="" disabled>Loading saved templates...</option>}
              </select>
            </div>
            <button
              type="button"
              className={buttonClass}
              onClick={applyTemplate}
              disabled={!selectedTemplateId}
            >
              Apply
            </button>
          </div>

          <div className="flex flex-col gap-3 md:flex-row">
            <input
              className={inputClass}
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Save current draft as..."
            />
            <button
              type="button"
              className={buttonClass}
              onClick={saveTemplate}
              disabled={savingTemplate}
            >
              {savingTemplate ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </section>

        <section className="border-t border-white/10 pt-4 space-y-3">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Style Draft</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Layout Variant</label>
              <select
                className={inputClass}
                value={draft.layoutVariant || ''}
                onChange={(e) =>
                  patchDraft('layoutVariant', e.target.value as CarouselStylePresetDraft['layoutVariant'], true)
                }
              >
                <option value="">Default</option>
                {carouselLayoutVariants.map((variant) => (
                  <option key={variant} value={variant}>
                    {formatLabel(variant)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Text Density</label>
              <select
                className={inputClass}
                value={draft.textDensity || ''}
                onChange={(e) =>
                  patchDraft('textDensity', e.target.value as CarouselStylePresetDraft['textDensity'], true)
                }
              >
                <option value="">Default</option>
                {carouselTextDensities.map((density) => (
                  <option key={density} value={density}>
                    {formatLabel(density)}
                  </option>
                ))}
              </select>
            </div>
            {[
              ['brandTag', 'Preset Brand Tag'],
              ['logoUrl', 'Logo URL'],
              ['highlightTextureUrl', 'Highlight Texture URL'],
              ['headlineFontId', 'Headline Font ID'],
              ['bodyFontId', 'Body Font ID'],
              ['highlightStart', 'Highlight Start'],
              ['highlightEnd', 'Highlight End'],
            ].map(([key, label]) => (
              <div key={key}>
                <label className={labelClass}>{label}</label>
                <input
                  className={inputClass}
                  value={fieldValue(draft[key as keyof CarouselStylePresetDraft])}
                  onChange={(e) =>
                    patchDraft(key as keyof CarouselStylePresetDraft, e.target.value)
                  }
                  onBlur={() => commitDraft(draft)}
                  placeholder={key.includes('highlight') ? '#ffffff' : undefined}
                />
              </div>
            ))}
            {[
              ['highlightIntensity', 'Highlight Intensity', '0.75', '1.8', '0.05'],
              ['highlightTextureStrength', 'Texture Strength', '0', '1.5', '0.05'],
              ['bottomOverlayStrength', 'Bottom Overlay', '0.35', '1.35', '0.05'],
            ].map(([key, label, min, max, step]) => (
              <div key={key}>
                <label className={labelClass}>{label}</label>
                <input
                  type="number"
                  min={min}
                  max={max}
                  step={step}
                  className={inputClass}
                  value={fieldValue(draft[key as keyof CarouselStylePresetDraft])}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    if (rawValue === '') {
                      patchDraft(key as keyof CarouselStylePresetDraft, undefined);
                      return;
                    }
                    const parsed = Number(rawValue);
                    if (Number.isFinite(parsed)) {
                      patchDraft(key as keyof CarouselStylePresetDraft, parsed);
                    }
                  }}
                  onBlur={(e) => commitNumber(key as keyof CarouselStylePresetDraft, e.target.value)}
                />
              </div>
            ))}
          </div>

          <label className="flex items-center gap-2 text-xs text-zinc-300 font-mono">
            <input
              type="checkbox"
              checked={Boolean(draft.showDecorativeLines)}
              onChange={(e) => patchDraft('showDecorativeLines', e.target.checked, true)}
              className="accent-white"
            />
            Show decorative lines
          </label>
        </section>

        <section className="border-t border-white/10 pt-4 space-y-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
              Proposal Drafts
            </p>
            <p className="text-[11px] text-zinc-600 font-mono mt-1">
              No generation endpoint is wired yet. This saves a proposal draft for the current style state.
            </p>
          </div>

          <AutoResizeTextarea
            className="w-full bg-zinc-900 border border-white/10 text-sm text-white px-3 py-2 resize-none focus:outline-none focus:border-white/25 font-sans"
            rows={3}
            value={proposalInstruction}
            onChange={(e) => setProposalInstruction(e.target.value)}
            placeholder="Describe the carousel style change..."
          />
          <button
            type="button"
            className={buttonClass}
            onClick={createProposal}
            disabled={savingProposal}
          >
            {savingProposal ? 'Saving...' : 'Save Proposal Draft'}
          </button>

          <div className="space-y-2">
            {proposals.length === 0 ? (
              <p className="text-xs text-zinc-600 font-mono">No proposals for this clip.</p>
            ) : (
              proposals.map((proposal) => (
                <div
                  key={proposal.id}
                  className="border border-white/10 bg-zinc-950 px-3 py-2 flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                        {proposal.status}
                      </span>
                      {proposal.created_at && (
                        <span className="text-[10px] text-zinc-700 font-mono">
                          {new Date(proposal.created_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-300 truncate">{proposal.instruction || 'Untitled proposal'}</p>
                  </div>
                  <button
                    type="button"
                    className={buttonClass}
                    onClick={() => applyProposal(proposal)}
                    disabled={!['draft', 'validated', 'approved'].includes(proposal.status)}
                  >
                    Apply
                  </button>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default CarouselSettingsInline;
